import { featureCatalog } from "./catalog";
import { siteFeatures } from "./sites";
import type { FeatureDefinition, FeatureNavItem, FeatureRoute, SiteId } from "./types";

const byId = new Map(featureCatalog.map((feature) => [feature.id, feature]));

/**
 * The features named by an arbitrary enablement list, in catalog order (so route precedence
 * is stable). Exported so a test can prove the central claim — that removing an id removes
 * exactly that feature and touches nothing else — without editing `sites.ts`.
 */
export function resolveFeatures(ids: readonly string[]): FeatureDefinition[] {
  const enabled = new Set(ids);
  return featureCatalog.filter((feature) => enabled.has(feature.id));
}

/** The features a site mounts, in catalog order (so route precedence is stable). */
export function featuresFor(site: SiteId): FeatureDefinition[] {
  return resolveFeatures(siteFeatures[site] ?? []);
}

export function hasFeature(site: SiteId, featureId: string): boolean {
  return (siteFeatures[site] ?? []).includes(featureId);
}

/**
 * Every route the site's features contribute. `public` routes are separated by the caller,
 * because a host mounts them outside its authenticated guard.
 */
export function routesFor(site: SiteId): FeatureRoute[] {
  return featuresFor(site).flatMap((feature) => feature.routes);
}

export function routePathsFor(site: SiteId): string[] {
  return routesFor(site).map((route) => route.path);
}

/**
 * The scene loader for one of a site's mounted paths. The generated route block calls this
 * instead of embedding an `import()` per route, so the loaders stay in the catalog — the
 * generated text carries nothing but paths.
 */
export function loaderFor(site: SiteId, path: string): FeatureRoute["load"] {
  const route = routesFor(site).find((candidate) => candidate.path === path);
  if (!route) {
    throw new Error(`${site} mounts no feature route for "${path}"`);
  }
  return route.load;
}

export function navFor(site: SiteId): FeatureNavItem[] {
  return featuresFor(site).flatMap((feature) => feature.nav ?? []);
}

export function navSectionFor(site: SiteId, section: string): FeatureNavItem[] {
  return navFor(site).filter((item) => item.section === section);
}

/** Every server endpoint the site's mounted features call. */
export function requiredBackendFor(site: SiteId): string[] {
  return [...new Set(featuresFor(site).flatMap((feature) => feature.requiredBackend ?? []))].sort();
}

/**
 * A typo in `sites.ts` mounts nothing and says nothing. This turns that into a failure —
 * the registry test calls it, so a bad id cannot reach a deploy.
 */
export function assertSitesResolve(): void {
  const unknown: string[] = [];
  for (const [site, ids] of Object.entries(siteFeatures)) {
    for (const id of ids) {
      if (!byId.has(id)) {
        unknown.push(`${site}: "${id}"`);
      }
    }
  }

  if (unknown.length > 0) {
    throw new Error(
      `sites.ts names features that are not in the catalog — ${unknown.join(", ")}`,
    );
  }
}
